// src/components/myAccount/pages/CustomPlanSubscriptionPage.js
// Dedicated page for custom/enterprise subscription plans via unique URL

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Check,
  Crown,
  Shield,
  Loader2,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Lock,
  Tag,
  X
} from 'lucide-react';
import stripeService from '../../../services/stripeService';
import { supabase } from '../../../config';
import { useGlobalStore } from '../../../state/globalStore';

const CustomPlanSubscriptionPage = () => {
  const { user, initUser } = useGlobalStore();
  const { token } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeValid, setPromoCodeValid] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [finalizingSession, setFinalizingSession] = useState(false);
  const [searchParams] = React.useState(() => new URLSearchParams(window.location.search));
  const sessionId = searchParams.get('session_id');

  // Initialize user on mount
  useEffect(() => {
    initUser();
  }, [initUser]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // Refresh user in store
        initUser();
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [initUser]);

  // Redirect to login if not authenticated when trying to subscribe
  // Allow viewing the plan page, but require login for subscription
  useEffect(() => {
    // Wait a bit for user state to initialize
    const checkAuth = setTimeout(() => {
      // Only redirect if user explicitly tries to subscribe without being logged in
      // We'll handle this in handleSubscribe instead to allow viewing the page
    }, 100);

    return () => clearTimeout(checkAuth);
  }, [user, navigate, token]);

  // Fetch plan by unique token
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        setLoading(true);
        setError('');

        // Fetch plan from database by unique_token
        const { data, error: fetchError } = await supabase
          .from('subscription_plans')
          .select('*')
          .eq('unique_token', token)
          .eq('is_active', true)
          .maybeSingle();

        if (fetchError || !data) {
          setError('This subscription link is invalid or has expired. Please contact support.');
          setLoading(false);
          return;
        }

        // Use features from database only (no redundant building from product_restrictions)
        const features = (data.features && Array.isArray(data.features) && data.features.length > 0) 
          ? data.features 
          : [];

        // Include all plan details from database
        setPlan({
          ...data,
          features,
          trial_days: data.trial_days || 0,
          description: data.description || ''
        });
      } catch (err) {
        console.error('Error fetching plan:', err);
        setError('Failed to load subscription plan. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPlan();
    }
  }, [token]);

  // Handle Stripe checkout session finalization
  useEffect(() => {
    if (!sessionId || !user?.id) return;

    let isCancelled = false;

    const finalizeSession = async () => {
      setFinalizingSession(true);
      setMessage('');
      setError('');

      try {
        const response = await stripeService.getCheckoutSession(sessionId, user.id);

        if (response?.success) {
          setMessage('Subscription activated! Redirecting to confirmation page...');
          // Redirect to success page with session ID
          setTimeout(() => {
            navigate(`/my-account/subscription-success?session_id=${sessionId}`);
          }, 1500);
        } else {
          setError('We could not verify your subscription. Please contact support if this persists.');
        }
      } catch (err) {
        setError(err.message || 'Failed to verify subscription. Please try again.');
      } finally {
        if (!isCancelled) {
          setFinalizingSession(false);
        }
      }
    };

    finalizeSession();

    return () => {
      isCancelled = true;
    };
  }, [sessionId, user?.id, navigate]);

  const handleSubscribe = async () => {
    // Require authentication before subscribing
    if (!user || !user.id) {
      setError('Please log in or create an account to subscribe.');
      // Redirect to login with return URL to come back after authentication
      navigate(`/login?return=${encodeURIComponent(`/subscribe/${token}`)}`);
      return;
    }

    if (!plan) {
      setError('Plan not loaded. Please refresh the page.');
      return;
    }

    setSubscribing(true);
    setError('');
    setMessage('');

    try {
      // Validate promo code if provided
      let validatedPromo = null;
      if (promoCode && promoCode.trim()) {
        try {
          const validation = await stripeService.validatePromoCode(promoCode, plan.id);
          if (validation.valid) {
            validatedPromo = {
              valid: true,
              code: validation.promo_code.code,
              id: validation.promo_code.id,
              type: validation.promo_code.type,
              value: validation.promo_code.value
            };
          } else {
            setError(validation.error || 'Invalid promo code for this plan');
            setSubscribing(false);
            return;
          }
        } catch (err) {
          setError(err.message || 'Error validating promo code');
          setSubscribing(false);
          return;
        }
      }

      const origin = window.location.origin;
      const session = await stripeService.createCheckoutSession({
        user_id: user.id,
        planId: plan.id,
        planType: plan.plan_type,
        billingCycle,
        promoCode: validatedPromo,
        success_url: `${origin}/subscribe/${token}?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${origin}/subscribe/${token}`
      });

      if (session?.url) {
        window.location.href = session.url;
      } else {
        throw new Error('Unable to start checkout session.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while starting checkout.');
    } finally {
      setSubscribing(false);
    }
  };

  const validatePromoCode = async (code) => {
    if (!code.trim()) {
      setPromoCodeValid(null);
      return;
    }
    
    setValidatingPromo(true);
    setError('');
    try {
      const validation = await stripeService.validatePromoCode(code, plan?.id || null);
      
      if (validation.valid) {
        setPromoCodeValid({
          valid: true,
          code: validation.promo_code.code,
          type: validation.promo_code.type,
          value: validation.promo_code.value,
          description: `Discount applied: ${validation.promo_code.type === 'percentage' ? validation.promo_code.value + '%' : '$' + (validation.promo_code.value / 100).toFixed(2)}`
        });
        setError('');
      } else {
        setPromoCodeValid({
          valid: false,
          message: validation.error || 'Invalid promo code'
        });
        setError(validation.error || 'Invalid promo code');
      }
    } catch (err) {
      setPromoCodeValid({
        valid: false,
        message: err.message || 'Error validating code'
      });
      setError(err.message || 'Error validating promo code');
    } finally {
      setValidatingPromo(false);
    }
  };

  const handlePromoCodeSubmit = (e) => {
    e.preventDefault();
    validatePromoCode(promoCode);
  };

  const formatPrice = (cents, planType = null) => {
    // Show "Custom" for Enterprise plans with zero price
    if (cents === 0 && planType === 'enterprise') {
      return 'Custom';
    }
    // Show "Free" for other plans with zero price
    if (cents === 0) return 'Free';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'enterprise': return Crown;
      case 'custom': return Lock;
      default: return Shield;
    }
  };

  if (loading || finalizingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">
            {finalizingSession ? 'Activating your subscription...' : 'Loading subscription plan...'}
          </p>
        </div>
      </div>
    );
  }

  if (error && !plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/my-account/subscriptions')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            View Available Plans
          </button>
        </div>
      </div>
    );
  }

  if (!plan) {
    return null;
  }

  // eslint-disable-next-line no-unused-vars
  const IconComponent = getPlanIcon(plan.plan_type);
  // eslint-disable-next-line no-unused-vars
  const monthlyPrice = billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly / 12;
  // eslint-disable-next-line no-unused-vars
  const yearlyPrice = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly * 12;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Left Column - Logo, Plan Name and Features */}
          <div className="flex flex-col space-y-6">
            {/* Logo */}
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">K</span>
              </div>
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Kapitalized</h1>
            </div>

            {/* Plan Title and Subtitle */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">
                {plan.name}
              </h2>
              <p className="text-lg text-gray-500 mb-4">
                Subscription plan to Kapitalized platform
              </p>
              {plan.description && (
                <p className="text-lg text-gray-600">
                  {plan.description}
                </p>
              )}
            </div>

            {/* Features */}
            {plan.features && plan.features.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Included</h3>
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Copyright */}
            <div className="mt-auto pt-8">
              <p className="text-sm text-gray-500">
                © Kapitalized 2025
              </p>
            </div>
          </div>

          {/* Right Column - Price, Subscribe, Help */}
          <div>
            <div className="bg-white rounded-lg shadow-xl p-8 sticky top-8">

              {/* Messages */}
              {message && (
                <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    {message}
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    {error}
                  </div>
                </div>
              )}
            {/* Billing Toggle */}
            <div className="flex items-center justify-center mb-8">
              <span className={`mr-3 ${billingCycle === 'monthly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Monthly
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={billingCycle === 'yearly'}
                  onChange={(e) => setBillingCycle(e.target.checked ? 'yearly' : 'monthly')}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              <span className={`ml-3 ${billingCycle === 'yearly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Yearly
              </span>
              {billingCycle === 'yearly' && plan.price_yearly > 0 && plan.price_monthly > 0 && (
                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                  Save {Math.round(((1 - (plan.price_yearly / (plan.price_monthly * 12))) * 100))}%
                </span>
              )}
            </div>

            {/* Price */}
            <div className="text-center mb-8">
              <div className="flex items-baseline justify-center">
                <span className="text-5xl font-bold text-gray-900">
                  {formatPrice(billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly, plan.plan_type)}
                </span>
                {plan.price_monthly > 0 && plan.plan_type !== 'enterprise' && (
                  <span className="ml-2 text-xl text-gray-500">
                    /{billingCycle === 'monthly' ? 'month' : 'year'}
                  </span>
                )}
              </div>
              {plan.price_monthly === 0 && plan.plan_type === 'enterprise' && (
                <p className="mt-2 text-sm text-gray-600">
                  Contact us for custom pricing
                </p>
              )}
              {plan.price_monthly > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-center gap-4 text-sm">
                    <span className="text-gray-600">
                      Monthly: <span className="font-semibold text-gray-900">{formatPrice(plan.price_monthly, plan.plan_type)}</span>
                    </span>
                    <span className="text-gray-400">|</span>
                    <span className="text-gray-600">
                      Yearly: <span className="font-semibold text-gray-900">{formatPrice(plan.price_yearly, plan.plan_type)}</span>
                    </span>
                  </div>
                  {plan.price_yearly > 0 && plan.price_monthly > 0 && (
                    <p className="text-xs text-gray-500">
                      Save {Math.round(((1 - (plan.price_yearly / (plan.price_monthly * 12))) * 100))}% with yearly billing
                    </p>
                  )}
                </div>
              )}
              {plan.trial_days > 0 && (
                <p className="mt-2 text-sm text-blue-600 font-medium">
                  {plan.trial_days}-day free trial included
                </p>
              )}
            </div>


            {/* Promo Code Section */}
            {plan.price_monthly > 0 && plan.plan_type !== 'enterprise' && (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => setShowPromoForm(!showPromoForm)}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Tag className="h-4 w-4" />
                  Have a promo code?
                </button>
                
                {showPromoForm && (
                  <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                    <form onSubmit={handlePromoCodeSubmit} className="flex space-x-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="Enter promo code"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={validatingPromo}
                      />
                      <button
                        type="submit"
                        disabled={validatingPromo || !promoCode.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {validatingPromo ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Apply'
                        )}
                      </button>
                    </form>
                    
                    {promoCodeValid && (
                      <div className={`mt-3 p-3 rounded-lg flex items-center justify-between ${
                        promoCodeValid.valid ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                      }`}>
                        <div className="flex items-center gap-2">
                          {promoCodeValid.valid ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <AlertCircle className="h-5 w-5" />
                          )}
                          <span className="text-sm">
                            {promoCodeValid.valid ? promoCodeValid.description : promoCodeValid.message}
                          </span>
                        </div>
                        {promoCodeValid.valid && (
                          <button
                            onClick={() => {
                              setPromoCode('');
                              setPromoCodeValid(null);
                            }}
                            className="text-green-800 hover:text-green-900"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

              {/* Subscribe Button */}
              <button
                onClick={!user || !user.id ? () => {
                  navigate(`/login?return=${encodeURIComponent(`/subscribe/${token}`)}`);
                } : handleSubscribe}
                disabled={subscribing}
                className="w-full py-4 bg-blue-600 text-white rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
              >
                {subscribing ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : !user || !user.id ? (
                  <>
                    <Lock className="h-5 w-5 mr-2" />
                    Log in to Subscribe
                  </>
                ) : (
                  <>
                    Subscribe Now
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </button>

              {!user && (
                <p className="mt-4 text-center text-sm text-gray-600">
                  <button
                    onClick={() => navigate(`/login?return=${encodeURIComponent(`/subscribe/${token}`)}`)}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Log in
                  </button>
                  {' or '}
                  <button
                    onClick={() => navigate(`/login?mode=signup&return=${encodeURIComponent(`/subscribe/${token}`)}`)}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    sign up
                  </button>
                  {' to subscribe'}
                </p>
              )}

              {/* Help Section */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Need Help?</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Questions about this plan? Our support team is here to help.
                </p>
                <a 
                  href="/contact" 
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-flex items-center"
                >
                  Contact Support
                  <ArrowRight className="h-4 w-4 ml-1" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomPlanSubscriptionPage;

